

import React from 'react';
import { CheckCircle2, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from "@/lib/data";

interface Task {
  id: number;
  title: string;
  due_date?: string;
  dueDate?: string;
  priority?: string;
  status?: string;
}

interface TasksWidgetProps {
  tasks: Task[];
  loading?: boolean;
}

const priorityColors = {
  high: 'text-red-600 bg-red-50',
  medium: 'text-yellow-600 bg-yellow-50',
  low: 'text-green-600 bg-green-50',
};

const statusIcons = {
  completed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
  pending: <Clock className="w-4 h-4 text-amber-500" />,
  overdue: <AlertCircle className="w-4 h-4 text-red-600" />,
};

export function TasksWidget({ tasks, loading = false }: TasksWidgetProps) {
  const displayTasks = tasks.slice(0, 5);

  const isOverdue = (dueDate: string | undefined) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-stone-800">Pending Tasks</h3>
        <Link to="/tasks" className="text-xs text-amber-600 hover:text-amber-700 font-medium">
          View all
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-stone-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : displayTasks.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-8">No pending tasks</p>
      ) : (
        <div className="space-y-2">
          {displayTasks.map((task) => {
            const dueDate = task.due_date || task.dueDate;
            const overdue = isOverdue(dueDate);
            const status = overdue ? 'overdue' : task.status || 'pending';

            return (
              <Link
                key={task.id}
                to={`/tasks/${task.id}`}
                className="block p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {statusIcons[status as keyof typeof statusIcons]}
                      <p className="font-medium text-sm truncate group-hover:text-amber-600">
                        {task.title}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                      {dueDate && (
                        <span className="text-xs text-stone-500">
                          Due: {formatDate(dueDate)}
                        </span>
                      )}
                      {task.priority && (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          priorityColors[task.priority as keyof typeof priorityColors] || ''
                        }`}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
